<!-- eslint-disable max-len -->
<template>
  <div>
    <ResendConfirmationOptions v-if="showResendOptions" />

    <div class="wrapper" v-else>
      <div class="wrapper-body" :class="{ component: render === 'component'}">
        <div class="wrapper-inner" :class="{ component: render === 'component'}">
          <img class="logo" src="https://cdn.parcelex.com.br/assets/v2/website/parcelex-logo-color-dark.svg">

          <p class="lead warning" v-if="device === 'desktop'">
            <ExclamationCircleIcon size="16" color="var(--yellow-600)" />
            Esta etapa só pode ser realizada através do celular cadastrado.
          </p>

          <template v-if="render === 'view'">
            <h2 class="main-title">
              Olá, <span class="text-primary-400">{{ name | capitalize({ first: true }) }}</span>
            </h2>
            <p class="lead">
              Insira o código de verificação enviado para o celular abaixo:
            </p>
          </template>

          <template v-else>
            <p class="lead" v-if="hasToSendCode">
              Para prosseguir, insira o código de verificação enviado para o celular abaixo:
            </p>

            <p class="lead" v-else>
              Para prosseguir, acesse o link para o número:
            </p>
          </template>

          <div class="text-center">
            <div class="phone-number">
              {{ phone | phoneMask({ lastDigits: true }) }}
            </div>
          </div>

          <VerificationCode
            v-if="hasToSendCode"
            ref="verification-code"
            class="verification-code"
            :length="6"
            @onComplete="completedVerificationCode"
          />

          <div v-if="checkingConfirmationCode && !successConfirmation" class="loading-message">
            <InlineSpinner
              :border="3"
              :width="20"
              :height="20"
              primaryColor="#94A3B8"
              secondaryColor="#E2E8F0"
            />
            <p>Verificando código...</p>
          </div>

          <div v-if="!checkingConfirmationCode && confirmationCodeChecked" class="loading-message">
            <div class="success" v-if="successConfirmation">
              <AssetV3 dir="icons" name="check-circle-success-small.svg" />
              <p>Código validado</p>
            </div>
            <div class="danger" v-else>
              <p>Código inválido</p>
            </div>
          </div>

          <div
            class="text-center link"
            v-if="!checkingConfirmationCode && !successConfirmation"
          >
            <a
              @click="resendConfirmationCode"
              :class="{ 'disabled': chosenConfirmationResendType }"
            >
              {{ resendText }} {{ resendCounter !== 0 ? `(${resendCounter}s)` : '' }}
            </a>
          </div>

          <p class="extra-info" v-if="device === 'desktop'">
            Somente para sua primeira compra no Parcelex é necessário fazer {{
              infoAboutDoc()
            }}. <br/>
          Também pode ser necessário refazer o envio caso você troque o número do seu celular. <br/>
          Está com problemas para acessar o link?
          Entre em contato no email atendimento@parcelex.com.br
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex';
import { ExclamationCircleIcon } from '@vue-hero-icons/solid';

import AssetV3 from '@/components/common/AssetV3.vue';
import VerificationCode from '@/components/common/VerificationCode.vue';
import InlineSpinner from '@/components/common/InlineSpinner.vue';
import StringMixing from '@/mixins/string';
import UtilMixin from '@/mixins/util';
import ResendConfirmationOptions from './ResendConfirmationOptions.vue';

export default {
  name: 'onboarding-confirmation',
  mixins: [StringMixing, UtilMixin],
  props: {
    render: {
      type: String,
      default: 'view',
    },
    device: {
      type: String,
      default: 'mobile',
    },
  },
  computed: {
    ...mapState('onboarding', [
      'name',
      'phone',
      'afterLoadingRoute',
      'transactionId',
      'onboardingToken',
      'bypassDocuments',
      'hasTrustedOnboarding',
      'chosenConfirmationResendType',
      'disableTheResendCounter',
      'showResendOptions',
      'confirmationResendType',
    ]),
    hasToSendCode() {
      return this.device === 'mobile' || this.hasTrustedOnboarding;
    },
  },
  data: () => ({
    resendCounter: 90,
    checkingConfirmationCode: false,
    confirmationCodeChecked: false,
    successConfirmation: null,
    securityCode: null,
    resendText: null,
  }),
  methods: {
    ...mapActions('onboarding', [
      'resendConfirmation',
      'sendConfirmationCode',
      'updateStep',
      'setShowResendOptions',
    ]),
    controllerResendCounter() {
      if (this.resendCounter !== 0) return;

      if (this.disableTheResendCounter) {
        this.resendCounter = 90;
        this.startResendCounter();
        this.setDisableTheResendCounter();
      }
    },
    resendConfirmationCode() {
      if (!this.chosenConfirmationResendType) {
        this.resendCounter = 90;
        this.setShowResendOptions({ showResendOptions: true });
        this.startResendCounter();
      }
    },
    startResendCounter() {
      const resendInterval = setInterval(() => {
        this.resendCounter -= 1;
        if (this.resendCounter === 0) {
          clearInterval(resendInterval);
          this.setChosenConfirmationResendType();
        }
      }, 1000);
    },
    async completedVerificationCode(securityCode) {
      this.checkingConfirmationCode = true;

      const { onboarding, success } = await this.sendConfirmationCode({ securityCode });

      this.checkingConfirmationCode = false;

      this.confirmationCodeChecked = true;
      this.successConfirmation = success;

      if (success) {
        setTimeout(() => {
          this.updateStep({ step: onboarding.step });

          const replaceNameRoute = this.isMobile() && !['ENTRY', 'COMPLETE'].includes(onboarding.step)
            ? 'onboarding-missing-steps'
            : this.afterLoadingRoute;

          return this.$router.replace({
            name: replaceNameRoute,
            query: {
              ...this.$route.query,
              transactionId: this.transactionId,
              onboardingToken: this.onboardingToken,
            },
          });
        }, 1000);
      } else {
        this.$refs['verification-code'].clearInput();
      }
    },
    infoAboutDoc() {
      const selfieText = 'o envio da selfie';
      const docSelfieText = 'e documentos através do seu celular cadastrado';

      return this.bypassDocuments ? selfieText : `${selfieText} ${docSelfieText}`;
    },
    ...mapMutations('onboarding', [
      'setChosenConfirmationResendType',
      'setDisableTheResendCounter',
    ]),
  },
  mounted() {
    this.resendText = this.hasToSendCode
      ? 'Não recebi o código'
      : 'Não recebi o link';

    this.controllerResendCounter();

    const { forceResendToken } = this.$route.query;
    if (forceResendToken) this.resendConfirmationCode();
    this.setChosenConfirmationResendType();
    this.startResendCounter();
  },
  components: {
    VerificationCode,
    InlineSpinner,
    AssetV3,
    ExclamationCircleIcon,
    ResendConfirmationOptions,
  },
};
</script>

  <style lang="scss" scoped>
    @import '../styles/index.scss';

    .wrapper {
      min-height: unset !important;
    }

    .verification-code {
      margin-bottom: 16px;
    }

    .loading-message {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;

      margin-bottom: 36px;

      p {
        color: var(--gray-500);
      }

      .success {
        display: flex;
        gap: 6px;

        justify-content: center;
        align-items: center;

        p {
          color: var(--green-500)
        }

        img {
          width: 16px;
          height: 16px;
        }
      }

      .danger {
        p {
          color: var(--red-300);
        }
      }
    }

    .extra-info {
      margin-top: 36px;

      font-size: 14px;
      line-height: 24px;
      font-weight: 400;

      color: var(--gray-500);
    }

    .link {
      -webkit-tap-highlight-color: transparent;

      .disabled {
        color: var(--gray-400);
        pointer-events: none;
      }
    }
  </style>
